/**
 * Gradebook.jsx — Phase 1E: Canonical Assessment & Gradebook
 *
 * Role-aware entry point:
 *   Admin / Registrar  → GradebookAdmin  (schemes, plans, submission review, published)
 *   Faculty / Instructor → GradebookFaculty (sections, mark entry, submit)
 *   Student             → GradebookStudent (published grades only)
 *
 * INVARIANT: No grade calculation in React. All computed_pct / grade_code values
 * come from the canonical backend. Frontend is presentation only.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  BookOpenCheck, ClipboardList, BarChart3, ChevronRight, ChevronDown,
  RefreshCw, Plus, Send, CheckCircle, XCircle, AlertTriangle,
  Lock, Unlock, Eye, Calculator, FileCheck, Award, Users,
  GraduationCap, BookOpen, Settings, ArrowLeft, Loader2
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─── Helper: extract error message from API response ──────────────────────────
function apiError(e, fallback = "An error occurred") {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  return String(detail);
}

function token() { return localStorage.getItem("academiaos_token"); }
function authH() { return { Authorization: `Bearer ${token()}` }; }

const PLAN_STATUS_STYLES = {
  DRAFT:      "bg-slate-700 text-slate-300 border-slate-600",
  PUBLISHED:  "bg-sky-900/60 text-sky-300 border-sky-700",
  LOCKED:     "bg-violet-900/60 text-violet-300 border-violet-700",
};

const BATCH_STATUS_STYLES = {
  CREATED:      "bg-slate-700 text-slate-300 border-slate-600",
  SUBMITTED:    "bg-amber-900/60 text-amber-300 border-amber-700",
  UNDER_REVIEW: "bg-orange-900/60 text-orange-300 border-orange-700",
  APPROVED:     "bg-emerald-900/60 text-emerald-300 border-emerald-700",
  REJECTED:     "bg-red-900/60 text-red-300 border-red-700",
  PUBLISHED:    "bg-violet-900/60 text-violet-300 border-violet-700",
};

const GRADE_RECORD_STATUS_STYLES = {
  DRAFT:        "bg-slate-700 text-slate-300",
  CALCULATED:   "bg-sky-900/60 text-sky-300",
  SUBMITTED:    "bg-amber-900/60 text-amber-300",
  APPROVED:     "bg-emerald-900/60 text-emerald-300",
  PUBLISHED:    "bg-violet-900/60 text-violet-300",
  UNDER_REVIEW: "bg-orange-900/60 text-orange-300",
};

const RESULT_STATUS_STYLES = {
  ASSESSED:   "text-white",
  ABSENT:     "bg-red-900/40 text-red-300 italic",
  EXEMPT:     "bg-slate-700 text-slate-300 italic",
  INCOMPLETE: "bg-amber-900/40 text-amber-300 italic",
  WITHHELD:   "bg-orange-900/40 text-orange-300 italic",
  PENDING:    "text-slate-500 italic",
};

function StatusBadge({ status, styles }) {
  const s = styles[status] || "bg-slate-700 text-slate-300 border-slate-600";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${s}`}
      data-testid={`status-badge-${status}`}>
      {status}
    </span>
  );
}

function Spinner() {
  return <Loader2 className="w-5 h-5 animate-spin text-amber-400 mx-auto" />;
}

function PageHeader({ icon: Icon, title, subtitle, onBack, action }) {
  return (
    <div className="flex items-start justify-between mb-8">
      <div className="flex items-start gap-3">
        {onBack && (
          <button onClick={onBack}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors mt-0.5"
            data-testid="gradebook-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="p-2 bg-slate-800 rounded-lg">
          <Icon className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">{title}</h1>
          {subtitle && <p className="text-sm text-slate-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

function SectionCard({ section, onClick }) {
  return (
    <button onClick={onClick}
      data-testid={`section-card-${section.section_id}`}
      className="w-full bg-slate-800 hover:bg-slate-750 border border-slate-700 hover:border-amber-600/40 rounded-xl p-4 text-left transition-all group">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-bold text-white">{section.course_title}</p>
          <p className="text-xs text-slate-400 mt-0.5">{section.section_code} · {section.term_code}</p>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 transition-colors mt-1" />
      </div>
      <div className="flex items-center gap-3 mt-3">
        <span className="text-xs text-slate-400 flex items-center gap-1">
          <Users className="w-3 h-3" /> {section.enrollment_count} enrolled
        </span>
        <span className="text-xs text-slate-400 flex items-center gap-1">
          <ClipboardList className="w-3 h-3" /> {section.plan_count} plan{section.plan_count !== 1 ? "s" : ""}
        </span>
        <StatusBadge status={section.section_status} styles={PLAN_STATUS_STYLES} />
      </div>
    </button>
  );
}

// ─── Workflow state pipeline visual ───────────────────────────────────────────
const BATCH_PIPELINE = ["CREATED", "SUBMITTED", "UNDER_REVIEW", "APPROVED", "PUBLISHED"];

function BatchPipeline({ status }) {
  const idx = BATCH_PIPELINE.indexOf(status);
  return (
    <div className="flex items-center gap-1 my-4" data-testid="batch-pipeline">
      {BATCH_PIPELINE.map((step, i) => (
        <React.Fragment key={step}>
          <div className={`flex flex-col items-center gap-1 ${i <= idx ? "" : "opacity-40"}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
              ${i < idx ? "bg-emerald-600 text-white" :
                i === idx ? "bg-amber-500 text-white ring-2 ring-amber-400/40" :
                "bg-slate-700 text-slate-400"}`}>
              {i < idx ? <CheckCircle className="w-3 h-3" /> : i + 1}
            </div>
            <span className="text-[10px] text-slate-500 hidden sm:block">{step}</span>
          </div>
          {i < BATCH_PIPELINE.length - 1 && (
            <div className={`flex-1 h-0.5 ${i < idx ? "bg-emerald-600" : "bg-slate-700"}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN VIEWS
// ─────────────────────────────────────────────────────────────────────────────

function SchemesPanel() {
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", scale_type: "PERCENTAGE" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/academic-admin/grading-schemes`, { headers: authH() });
      setSchemes(data.grading_schemes || []);
    } catch { toast.error("Failed to load grading schemes"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await axios.post(`${API}/academic-admin/grading-schemes`, form, { headers: authH() });
      toast.success("Grading scheme created");
      setCreating(false);
      setForm({ code: "", name: "", scale_type: "PERCENTAGE" });
      load();
    } catch (e) { toast.error(apiError(e, "Failed to create scheme")); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Grading Schemes</h3>
        <button onClick={() => setCreating(!creating)}
          data-testid="create-scheme-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-medium transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Scheme
        </button>
      </div>

      {creating && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Code</label>
              <input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))}
                placeholder="e.g. ISB-2026-PGP"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="scheme-code-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Name</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. PGP 2026 Grading"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="scheme-name-input" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Scale Type</label>
            <select value={form.scale_type} onChange={e => setForm(p => ({ ...p, scale_type: e.target.value }))}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="scheme-scale-select">
              <option value="PERCENTAGE">PERCENTAGE</option>
              <option value="GPA_4">GPA 4.0</option>
              <option value="GPA_10">GPA 10.0</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={create}
              data-testid="save-scheme-btn"
              className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors">
              Save
            </button>
            <button onClick={() => setCreating(false)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="space-y-2">
          {schemes.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">No grading schemes yet.</p>
          )}
          {schemes.map(s => (
            <div key={s.id}
              data-testid={`scheme-row-${s.id}`}
              className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">{s.name}</p>
                <p className="text-xs text-slate-400">{s.code} · {s.scale_type}</p>
              </div>
              <StatusBadge status={s.status} styles={PLAN_STATUS_STYLES} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SubmissionReviewPanel() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [remarks, setRemarks] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/academic-admin/gradebook/batches/pending-review`, { headers: authH() });
      setBatches(data.batches || []);
    } catch (e) {
      // endpoint may return 404 if no pending batches; treat gracefully
      if (e?.response?.status !== 404) toast.error("Failed to load pending batches");
      setBatches([]);
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const decide = async (batchId, decision) => {
    const b = batches.find(x => x.batch_id === batchId);
    try {
      await axios.post(`${API}/academic-admin/gradebook/submission-batches/${batchId}/decide`,
        { decision, decision_reason: remarks || `${decision} by Registrar`, version: b?.version ?? 1 },
        { headers: authH() });
      toast.success(`Batch ${decision}`);
      setDeciding(null);
      setRemarks("");
      load();
    } catch (e) { toast.error(apiError(e, `Failed to ${decision}`)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Pending Grade Submissions</h3>
        <button onClick={load} className="text-slate-400 hover:text-white transition-colors" data-testid="refresh-batches-btn">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {loading ? <Spinner /> : (
        <div className="space-y-3">
          {batches.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <FileCheck className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No grade submissions pending review.</p>
            </div>
          )}
          {batches.map(b => (
            <div key={b.batch_id} data-testid={`batch-review-${b.batch_id}`}
              className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-sm font-semibold text-white">Batch {b.batch_id?.slice(0, 8)}…</p>
                  <p className="text-xs text-slate-400 mt-0.5">Section: {b.section_id?.slice(0, 8)}… · Submitted by: {b.submitted_by?.slice(0, 8)}…</p>
                </div>
                <StatusBadge status={b.status} styles={BATCH_STATUS_STYLES} />
              </div>
              <BatchPipeline status={b.status} />
              {b.status === "UNDER_REVIEW" && (
                <div className="mt-3 space-y-2">
                  {deciding === b.batch_id ? (
                    <div className="space-y-2">
                      <input value={remarks} onChange={e => setRemarks(e.target.value)}
                        placeholder="Decision remarks (optional)"
                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                        data-testid="decision-remarks-input" />
                      <div className="flex gap-2">
                        <button onClick={() => decide(b.batch_id, "APPROVED")}
                          data-testid={`approve-batch-${b.batch_id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-xs font-medium transition-colors">
                          <CheckCircle className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button onClick={() => decide(b.batch_id, "REJECTED")}
                          data-testid={`reject-batch-${b.batch_id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-800 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition-colors">
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                        <button onClick={() => setDeciding(null)}
                          className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs font-medium transition-colors">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setDeciding(b.batch_id)}
                      data-testid={`review-action-btn-${b.batch_id}`}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-medium transition-colors">
                      <Eye className="w-3.5 h-3.5" /> Review & Decide
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GradebookAdmin() {
  const [activeTab, setActiveTab] = useState("sections");
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [view, setView] = useState("list"); // list | section | plan

  const loadSections = useCallback(async () => {
    setLoadingSections(true);
    try {
      const { data } = await axios.get(`${API}/academic-admin/gradebook/sections`, { headers: authH() });
      setSections(data.sections || []);
    } catch { toast.error("Failed to load sections"); }
    finally { setLoadingSections(false); }
  }, []);

  const loadSchemes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/academic-admin/grading-schemes`, { headers: authH() });
      setSchemes(data.grading_schemes || []);
    } catch {}
  }, []);

  const loadPlans = useCallback(async (sectionId) => {
    try {
      const { data } = await axios.get(`${API}/academic-admin/gradebook/sections/${sectionId}/plans`, { headers: authH() });
      setPlans(data.plans || []);
    } catch { toast.error("Failed to load plans"); }
  }, []);

  useEffect(() => {
    loadSections();
    loadSchemes();
  }, [loadSections, loadSchemes]);

  const selectSection = (s) => {
    setSelectedSection(s);
    loadPlans(s.section_id);
    setView("section");
  };

  const selectPlan = (p) => {
    setSelectedPlan(p);
    setView("plan");
  };

  const tabs = [
    { id: "sections", label: "Assessment Plans", icon: ClipboardList },
    { id: "schemes",  label: "Grading Schemes",  icon: Settings },
    { id: "review",   label: "Submission Review", icon: FileCheck },
  ];

  return (
    <div>
      <PageHeader
        icon={BookOpenCheck}
        title="Gradebook — Academic Admin"
        subtitle="Manage assessment plans, grading schemes, and grade approvals"
        onBack={view !== "list" ? () => { setView("list"); setSelectedSection(null); setSelectedPlan(null); } : null}
      />

      {/* Tab bar */}
      {view === "list" && (
        <div className="flex items-center gap-1 mb-6 bg-slate-800/50 rounded-xl p-1 border border-slate-700">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-1 justify-center
                ${activeTab === t.id ? "bg-slate-700 text-white" : "text-slate-400 hover:text-white"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      )}

      {view === "list" && (
        <>
          {activeTab === "sections" && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-400">Select a section to manage its assessment plan</p>
                <button onClick={loadSections} className="text-slate-400 hover:text-white" data-testid="refresh-sections-btn">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              {loadingSections ? <div className="py-8"><Spinner /></div> : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {sections.length === 0 && (
                    <p className="text-sm text-slate-500 col-span-2 text-center py-12">No sections with enrollments found.</p>
                  )}
                  {sections.map(s => (
                    <SectionCard key={s.section_id} section={s} onClick={() => selectSection(s)} />
                  ))}
                </div>
              )}
            </div>
          )}
          {activeTab === "schemes" && <SchemesPanel />}
          {activeTab === "review" && <SubmissionReviewPanel />}
        </>
      )}

      {view === "section" && selectedSection && (
        <SectionPlanManager
          section={selectedSection}
          plans={plans}
          schemes={schemes}
          onSelectPlan={selectPlan}
          onRefresh={() => loadPlans(selectedSection.section_id)}
        />
      )}

      {view === "plan" && selectedPlan && selectedSection && (
        <PlanDetailManager
          plan={selectedPlan}
          section={selectedSection}
          onRefresh={() => {
            loadPlans(selectedSection.section_id);
          }}
        />
      )}
    </div>
  );
}

// ─── Section → Plan Manager ───────────────────────────────────────────────────

function SectionPlanManager({ section, plans, schemes, onSelectPlan, onRefresh }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    code: "", title: "", grading_scheme_id: "",
    absence_treatment: "ZERO_CONTRIBUTION", description: ""
  });

  // Keep grading_scheme_id in sync when schemes load
  useEffect(() => {
    if (schemes.length > 0 && !form.grading_scheme_id) {
      setForm(p => ({ ...p, grading_scheme_id: schemes[0].id }));
    }
  }, [schemes, form.grading_scheme_id]);

  const createPlan = async () => {
    try {
      await axios.post(`${API}/academic-admin/gradebook/sections/${section.section_id}/plans`,
        { ...form }, { headers: authH() });
      toast.success("Assessment plan created");
      setCreating(false);
      onRefresh();
    } catch (e) { toast.error(apiError(e, "Failed to create plan")); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-bold text-white">{section.course_title}</h2>
          <p className="text-sm text-slate-400">{section.section_code} · {section.term_code} · {section.enrollment_count} students</p>
        </div>
        <button onClick={() => setCreating(!creating)}
          data-testid="create-plan-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-sm font-medium transition-colors">
          <Plus className="w-3.5 h-3.5" /> New Plan
        </button>
      </div>

      {creating && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Plan Code</label>
              <input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))}
                placeholder="e.g. PGP-2026-FIN301"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="plan-code-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Title</label>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Financial Accounting 2026"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="plan-title-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Grading Scheme</label>
              <select value={form.grading_scheme_id} onChange={e => setForm(p => ({ ...p, grading_scheme_id: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="plan-scheme-select">
                {schemes.map(s => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Absence Treatment</label>
              <select value={form.absence_treatment} onChange={e => setForm(p => ({ ...p, absence_treatment: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="plan-absence-select">
                <option value="ZERO_CONTRIBUTION">ZERO_CONTRIBUTION</option>
                <option value="EXCLUDE_COMPONENT">EXCLUDE_COMPONENT</option>
                <option value="BLOCK_GRADE_CALCULATION">BLOCK_GRADE_CALCULATION</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={createPlan} data-testid="save-plan-btn"
              className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors">
              Create Plan
            </button>
            <button onClick={() => setCreating(false)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {plans.length === 0 && !creating && (
          <p className="text-sm text-slate-500 text-center py-12">No assessment plans for this section yet.</p>
        )}
        {plans.map(p => (
          <button key={p.id} onClick={() => onSelectPlan(p)}
            data-testid={`plan-row-${p.id}`}
            className="w-full bg-slate-800 border border-slate-700 hover:border-amber-600/40 rounded-xl p-4 text-left transition-all group">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-bold text-white">{p.title}</p>
                <p className="text-xs text-slate-400 mt-0.5">{p.code} · v{p.version_no}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={p.status} styles={PLAN_STATUS_STYLES} />
                <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400 transition-colors" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Total weight: {p.total_weight_pct ?? 0}% · Absence: {p.absence_treatment}
            </p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Plan Detail — components + publish ──────────────────────────────────────

function PlanDetailManager({ plan, section, onRefresh }) {
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingComp, setAddingComp] = useState(false);
  const [compForm, setCompForm] = useState({ code: "", title: "", assessment_type: "ASSIGNMENT", weight_pct: 25, max_marks: 100, sequence: 1 });
  const [publishing, setPublishing] = useState(false);

  const loadComponents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/academic-admin/gradebook/plans/${plan.id}/components`, { headers: authH() });
      setComponents(data.components || []);
    } catch { toast.error("Failed to load components"); }
    finally { setLoading(false); }
  }, [plan.id]);

  useEffect(() => { loadComponents(); }, [loadComponents]);

  const addComponent = async () => {
    try {
      await axios.post(`${API}/academic-admin/gradebook/plans/${plan.id}/components`,
        compForm, { headers: authH() });
      toast.success("Component added");
      setAddingComp(false);
      loadComponents();
    } catch (e) { toast.error(apiError(e, "Failed to add component")); }
  };

  const publishPlan = async () => {
    if (plan.status !== "DRAFT") return;
    setPublishing(true);
    try {
      await axios.post(`${API}/academic-admin/gradebook/plans/${plan.id}/publish`, {}, { headers: authH() });
      toast.success("Plan published — faculty can now enter marks");
      onRefresh();
    } catch (e) { toast.error(apiError(e, "Failed to publish plan")); }
    finally { setPublishing(false); }
  };

  const totalWeight = components.reduce((s, c) => s + (c.weight_pct || 0), 0);

  return (
    <div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-base font-bold text-white">{plan.title}</h2>
              <StatusBadge status={plan.status} styles={PLAN_STATUS_STYLES} />
            </div>
            <p className="text-xs text-slate-400">{plan.code} · Absence: {plan.absence_treatment}</p>
            <p className="text-xs text-slate-400 mt-0.5">Section: {section.course_title} / {section.section_code}</p>
          </div>
          {plan.status === "DRAFT" && (
            <button onClick={publishPlan} disabled={publishing || totalWeight !== 100}
              data-testid="publish-plan-btn"
              title={totalWeight !== 100 ? "Total weight must equal 100% before publishing" : ""}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-sky-700 hover:bg-sky-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
              Publish Plan
            </button>
          )}
          {plan.status === "LOCKED" && (
            <div className="flex items-center gap-1.5 text-violet-400 text-xs font-medium">
              <Lock className="w-3.5 h-3.5" /> Plan Locked
            </div>
          )}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-700">
          <div className={`text-xs font-medium ${totalWeight === 100 ? "text-emerald-400" : "text-amber-400"}`}>
            Total weight: {totalWeight}% {totalWeight === 100 ? "✓" : `— need ${100 - totalWeight}% more`}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">Components</h3>
        {plan.status === "DRAFT" && (
          <button onClick={() => setAddingComp(!addingComp)}
            data-testid="add-component-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-medium transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add Component
          </button>
        )}
      </div>

      {addingComp && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Code</label>
              <input value={compForm.code} onChange={e => setCompForm(p => ({ ...p, code: e.target.value }))}
                placeholder="e.g. MID-EXAM"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="comp-code-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Title</label>
              <input value={compForm.title} onChange={e => setCompForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Mid-Semester Exam"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="comp-name-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Type</label>
              <select value={compForm.assessment_type} onChange={e => setCompForm(p => ({ ...p, assessment_type: e.target.value }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="comp-type-select">
                {["ASSIGNMENT", "QUIZ", "MID_SEMESTER_EXAM", "FINAL_EXAM", "PROJECT", "PRESENTATION", "PARTICIPATION", "PRACTICAL", "VIVA", "ATTENDANCE", "OTHER"].map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Weight %</label>
              <input type="number" min="0" max="100" value={compForm.weight_pct}
                onChange={e => setCompForm(p => ({ ...p, weight_pct: Number(e.target.value) }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="comp-weight-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Max Marks</label>
              <input type="number" min="1" value={compForm.max_marks}
                onChange={e => setCompForm(p => ({ ...p, max_marks: Number(e.target.value) }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="comp-max-marks-input" />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Sequence</label>
              <input type="number" min="1" value={compForm.sequence}
                onChange={e => setCompForm(p => ({ ...p, sequence: Number(e.target.value) }))}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="comp-seq-input" />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addComponent} data-testid="save-component-btn"
              className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-sm font-medium transition-colors">
              Add
            </button>
            <button onClick={() => setAddingComp(false)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="py-8"><Spinner /></div> : (
        <div className="space-y-2">
          {components.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">No components yet. Add at least one before publishing.</p>
          )}
          {components.map(c => (
            <div key={c.id} data-testid={`comp-row-${c.id}`}
              className="bg-slate-800 border border-slate-700 rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-white">{c.title}</p>
                <p className="text-xs text-slate-400">{c.assessment_type} · Max {c.max_marks} marks</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-amber-400">{c.weight_pct}%</p>
                <p className="text-xs text-slate-500">weight</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FACULTY VIEWS
// ─────────────────────────────────────────────────────────────────────────────

function GradebookFaculty() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("sections"); // sections | gradebook
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);

  const loadSections = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/academic-admin/gradebook/sections`, { headers: authH() });
      setSections((data.sections || []).filter(s => s.enrollment_count > 0));
    } catch { toast.error("Failed to load sections"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSections(); }, [loadSections]);

  const selectSection = async (s) => {
    setSelectedSection(s);
    try {
      const { data } = await axios.get(`${API}/academic-admin/gradebook/sections/${s.section_id}/plans`, { headers: authH() });
      const publishedPlans = (data.plans || []).filter(p => p.status !== "DRAFT");
      setPlans(publishedPlans);
      if (publishedPlans.length === 1) {
        setSelectedPlan(publishedPlans[0]);
        setView("gradebook");
      } else {
        setView("plans");
      }
    } catch { toast.error("Failed to load plans"); }
  };

  const goBack = () => {
    if (view === "gradebook") { setView("plans"); setSelectedPlan(null); }
    else { setView("sections"); setSelectedSection(null); setPlans([]); }
  };

  return (
    <div>
      <PageHeader
        icon={BookOpenCheck}
        title="Gradebook — Faculty"
        subtitle="Enter marks and submit grades for your sections"
        onBack={view !== "sections" ? goBack : null}
      />

      {view === "sections" && (
        <div>
          <p className="text-sm text-slate-400 mb-4">Select a section to enter marks</p>
          {loading ? <div className="py-8"><Spinner /></div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sections.length === 0 && (
                <div className="col-span-2 text-center py-12">
                  <GraduationCap className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                  <p className="text-sm text-slate-500">No sections with active enrollments found.</p>
                </div>
              )}
              {sections.map(s => <SectionCard key={s.section_id} section={s} onClick={() => selectSection(s)} />)}
            </div>
          )}
        </div>
      )}

      {view === "plans" && selectedSection && (
        <div>
          <div className="mb-4">
            <h2 className="text-base font-bold text-white">{selectedSection.course_title}</h2>
            <p className="text-sm text-slate-400">{selectedSection.section_code} · {selectedSection.term_code}</p>
          </div>
          {plans.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-600" />
              <p className="text-sm text-slate-500">No published assessment plans for this section.</p>
              <p className="text-xs text-slate-600 mt-1">Ask Registrar/Admin to publish an assessment plan first.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {plans.map(p => (
                <button key={p.id} onClick={() => { setSelectedPlan(p); setView("gradebook"); }}
                  data-testid={`faculty-plan-select-${p.id}`}
                  className="w-full bg-slate-800 border border-slate-700 hover:border-amber-600/40 rounded-xl p-4 text-left transition-all group">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-bold text-white">{p.title}</p>
                      <p className="text-xs text-slate-400">{p.code}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={p.status} styles={PLAN_STATUS_STYLES} />
                      <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-amber-400" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "gradebook" && selectedSection && selectedPlan && (
        <FacultyGradebookGrid section={selectedSection} plan={selectedPlan} />
      )}
    </div>
  );
}

// ─── Faculty Gradebook Grid ────────────────────────────────────────────────────

function FacultyGradebookGrid({ section, plan }) {
  const [components, setComponents] = useState([]);
  const [results, setResults] = useState({}); // { enrollment_id: { comp_id: result } }
  const [gradeRecords, setGradeRecords] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [editCell, setEditCell] = useState(null); // { enrollment_id, component_id }
  const [editValue, setEditValue] = useState("");
  const [editStatus, setEditStatus] = useState("ASSESSED");
  const [submittingBatch, setSubmittingBatch] = useState(false);
  const [batch, setBatch] = useState(null);
  const [publishingFinal, setPublishingFinal] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [compR, resR, gradeR] = await Promise.all([
        axios.get(`${API}/academic-admin/gradebook/plans/${plan.id}/components`, { headers: authH() }),
        axios.get(`${API}/academic-admin/gradebook/plans/${plan.id}/results`, { headers: authH() }),
        axios.get(`${API}/academic-admin/gradebook/plans/${plan.id}/grade-records`, { headers: authH() }),
      ]);
      setComponents(compR.data.components || []);
      setGradeRecords(gradeR.data.grade_records || []);

      // Build enrollment list from grade_records
      const enrMap = {};
      (gradeR.data.grade_records || []).forEach(gr => {
        enrMap[gr.enrollment_id] = { enrollment_id: gr.enrollment_id, student_name: gr.student_name || "Student" };
      });
      setEnrollments(Object.values(enrMap));

      // Index results by enrollment_id → component_id
      const resMap = {};
      (resR.data.results || []).forEach(r => {
        if (!resMap[r.enrollment_id]) resMap[r.enrollment_id] = {};
        resMap[r.enrollment_id][r.component_id] = r;
      });
      setResults(resMap);

      // Load current batch state for pipeline display
      try {
        const batchR = await axios.get(
          `${API}/academic-admin/gradebook/sections/${section.section_id}/submission-batches?plan_id=${plan.id}`,
          { headers: authH() });
        const batches = batchR.data.batches || [];
        // Show latest non-rejected batch
        const active = batches.find(b => !["REJECTED"].includes(b.status)) || batches[0] || null;
        setBatch(active);
      } catch {}
    } catch (e) { toast.error("Failed to load gradebook data"); }
    finally { setLoading(false); }
  }, [plan.id, section.section_id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveResult = async () => {
    if (!editCell) return;
    const { enrollment_id, component_id } = editCell;
    const payload = {
      enrollment_id,
      result_status: editStatus,
      marks_awarded: editStatus === "ASSESSED" ? Number(editValue) : null,
      evaluation_authority: "HUMAN",
    };
    try {
      await axios.put(`${API}/academic-admin/gradebook/components/${component_id}/results`,
        payload, { headers: authH() });
      toast.success("Mark saved");
      setEditCell(null);
      loadAll();
    } catch (e) { toast.error(apiError(e, "Failed to save mark")); }
  };

  const computeGrades = async () => {
    setComputing(true);
    try {
      const { data } = await axios.post(
        `${API}/academic-admin/gradebook/plans/${plan.id}/compute-grades`,
        {}, { headers: authH() });
      toast.success(`Grades computed for ${data.computed_count} students`);
      loadAll();
    } catch (e) { toast.error(apiError(e, "Compute failed")); }
    finally { setComputing(false); }
  };

  const submitForReview = async () => {
    setSubmittingBatch(true);
    try {
      // Step 1: Create DRAFT batch
      const { data: batchData } = await axios.post(
        `${API}/academic-admin/gradebook/sections/${section.section_id}/submission-batches`,
        { plan_id: plan.id, submission_notes: "Faculty grade submission" },
        { headers: authH() });
      const batchId = batchData.id; // note: backend returns "id" not "batch_id"
      // Step 2: Submit for review
      await axios.post(
        `${API}/academic-admin/gradebook/submission-batches/${batchId}/submit`,
        { submission_notes: "Submitted for Registrar review" },
        { headers: authH() });
      toast.success("Grades submitted for Registrar review");
      loadAll();
    } catch (e) { toast.error(apiError(e, "Submission failed")); }
    finally { setSubmittingBatch(false); }
  };

  const publishFinalGrades = async () => {
    setPublishingFinal(true);
    try {
      // Find the APPROVED batch for this plan
      const batchR = await axios.get(
        `${API}/academic-admin/gradebook/sections/${section.section_id}/submission-batches?plan_id=${plan.id}`,
        { headers: authH() });
      const approvedBatch = (batchR.data.batches || []).find(b => b.status === "APPROVED");
      if (!approvedBatch) {
        toast.error("No approved batch found. Please wait for Registrar approval.");
        return;
      }
      const { data } = await axios.post(
        `${API}/academic-admin/gradebook/submission-batches/${approvedBatch.batch_id}/publish`,
        {}, { headers: authH() });
      toast.success(`Final grades published for ${data.published_count} students`);
      loadAll();
    } catch (e) { toast.error(apiError(e, "Failed to publish")); }
    finally { setPublishingFinal(false); }
  };

  const allCalculated = gradeRecords.length > 0 && gradeRecords.every(gr =>
    ["CALCULATED", "SUBMITTED", "APPROVED", "PUBLISHED"].includes(gr.computation_status));
  const anyApproved = gradeRecords.some(gr => gr.computation_status === "APPROVED");
  const planStatus = plan.status;

  if (loading) return <div className="py-12"><Spinner /></div>;

  return (
    <div>
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-base font-bold text-white">{plan.title}</h2>
              <StatusBadge status={plan.status} styles={PLAN_STATUS_STYLES} />
            </div>
            <p className="text-xs text-slate-400">{section.course_title} / {section.section_code} · {enrollments.length} students</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadAll} className="text-slate-400 hover:text-white" data-testid="refresh-gradebook-btn">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Grade records summary */}
      {gradeRecords.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {["DRAFT", "CALCULATED", "APPROVED", "PUBLISHED"].map(st => {
            const count = gradeRecords.filter(gr => gr.computation_status === st).length;
            return (
              <div key={st} className="bg-slate-800 border border-slate-700 rounded-xl p-3 text-center">
                <div className={`text-xl font-bold ${count > 0 ? "text-amber-400" : "text-slate-600"}`}>{count}</div>
                <div className="text-xs text-slate-400 mt-0.5">{st}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Gradebook grid */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden mb-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="gradebook-grid">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Student</th>
                {components.map(c => (
                  <th key={c.id} className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                    <div>{c.title}</div>
                    <div className="text-amber-400/70">{c.weight_pct}% / {c.max_marks}</div>
                  </th>
                ))}
                <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">%</th>
                <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Grade</th>
                <th className="text-center px-3 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody>
              {enrollments.length === 0 && (
                <tr><td colSpan={components.length + 4} className="text-center py-8 text-slate-500">No enrolled students.</td></tr>
              )}
              {enrollments.map(enr => {
                const gr = gradeRecords.find(r => r.enrollment_id === enr.enrollment_id);
                return (
                  <tr key={enr.enrollment_id} className="border-t border-slate-700/50 hover:bg-slate-750">
                    <td className="px-4 py-2 text-sm text-white font-medium">
                      {gr?.student_name || enr.enrollment_id.slice(0, 8) + "…"}
                    </td>
                    {components.map(c => {
                      const res = results[enr.enrollment_id]?.[c.id];
                      const isEditing = editCell?.enrollment_id === enr.enrollment_id && editCell?.component_id === c.id;
                      const canEdit = plan.status === "PUBLISHED";

                      if (isEditing) {
                        return (
                          <td key={c.id} className="px-2 py-1">
                            <div className="flex flex-col gap-1">
                              <select value={editStatus} onChange={e => setEditStatus(e.target.value)}
                                className="bg-slate-900 border border-amber-500 rounded px-1 py-0.5 text-xs text-white w-full"
                                data-testid="result-status-select">
                                {["ASSESSED", "ABSENT", "EXEMPT", "INCOMPLETE", "WITHHELD"].map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                              {editStatus === "ASSESSED" && (
                                <input type="number" value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  min="0" max={c.max_marks}
                                  className="bg-slate-900 border border-amber-500 rounded px-2 py-0.5 text-xs text-white w-full"
                                  data-testid="marks-input"
                                  onKeyDown={e => { if (e.key === "Enter") saveResult(); if (e.key === "Escape") setEditCell(null); }} />
                              )}
                              <div className="flex gap-1">
                                <button onClick={saveResult}
                                  className="flex-1 bg-emerald-700 hover:bg-emerald-600 text-white rounded px-1 py-0.5 text-xs"
                                  data-testid="save-mark-btn">✓</button>
                                <button onClick={() => setEditCell(null)}
                                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white rounded px-1 py-0.5 text-xs">✕</button>
                              </div>
                            </div>
                          </td>
                        );
                      }

                      return (
                        <td key={c.id} className="px-3 py-2 text-center"
                          data-testid={`cell-${enr.enrollment_id}-${c.id}`}>
                          <button
                            disabled={!canEdit}
                            onClick={() => {
                              setEditCell({ enrollment_id: enr.enrollment_id, component_id: c.id });
                              setEditValue(res?.marks_awarded ?? "");
                              setEditStatus(res?.result_status || "ASSESSED");
                            }}
                            className={`min-w-[48px] px-2 py-0.5 rounded text-xs font-medium
                              ${!canEdit ? "cursor-default" : "cursor-pointer hover:bg-slate-700"}
                              ${res ? RESULT_STATUS_STYLES[res.result_status] : "text-slate-600 italic"}`}>
                            {!res ? "—" :
                              res.result_status === "ABSENT" ? "ABS" :
                              res.result_status === "EXEMPT" ? "EXM" :
                              res.result_status === "ASSESSED" ? res.marks_awarded : res.result_status.slice(0, 3)}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center text-sm font-bold text-white">
                      {gr?.computed_pct != null ? `${parseFloat(gr.computed_pct).toFixed(1)}%` : "—"}
                    </td>
                    <td className="px-3 py-2 text-center text-sm font-bold text-amber-400">
                      {gr?.grade_code || "—"}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {gr && <StatusBadge status={gr.computation_status} styles={GRADE_RECORD_STATUS_STYLES} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action bar — NO calculation happens here, only triggers backend */}
      {planStatus !== "LOCKED" && (
        <div className="flex flex-wrap items-center gap-3 bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-slate-400">
              All calculations are performed by the canonical backend.
              React only displays values returned by the server.
            </p>
          </div>
          <button onClick={computeGrades} disabled={computing}
            data-testid="compute-grades-btn"
            className="flex items-center gap-2 px-4 py-2 bg-sky-700 hover:bg-sky-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
            {computing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
            Compute Grades
          </button>
          {allCalculated && (
            <button onClick={submitForReview} disabled={submittingBatch}
              data-testid="submit-grades-btn"
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
              {submittingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit for Review
            </button>
          )}
          {anyApproved && planStatus !== "LOCKED" && (
            <button onClick={publishFinalGrades} disabled={publishingFinal}
              data-testid="publish-final-btn"
              className="flex items-center gap-2 px-4 py-2 bg-violet-700 hover:bg-violet-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors">
              {publishingFinal ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
              Publish Final Grades
            </button>
          )}
        </div>
      )}
      {planStatus === "LOCKED" && (
        <div className="flex items-center gap-2 bg-violet-900/20 border border-violet-700/40 rounded-xl p-4 text-violet-300 text-sm">
          <Lock className="w-4 h-4 flex-shrink-0" />
          This assessment plan is LOCKED. Final grades have been published. No further changes allowed.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STUDENT VIEW
// ─────────────────────────────────────────────────────────────────────────────

function GradebookStudent() {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/academic/me/grades`, { headers: authH() });
      setGrades(data.grades || []);
    } catch (e) {
      if (e?.response?.status !== 404) toast.error("Failed to load grades");
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader
        icon={GraduationCap}
        title="My Grades"
        subtitle="Published academic grades only. Grades under review are not shown."
        action={
          <button onClick={load} className="text-slate-400 hover:text-white" data-testid="refresh-my-grades-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        }
      />

      <div className="bg-amber-900/20 border border-amber-700/40 rounded-xl p-3 mb-6 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-300">
          Only officially published grades are shown here. Grades in draft, submitted, or under-review state
          are not visible to students.
        </p>
      </div>

      {loading ? <div className="py-12"><Spinner /></div> : (
        <div data-testid="my-grades-container">
          {grades.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-600" />
              <p className="text-base text-slate-500">No published grades yet.</p>
              <p className="text-sm text-slate-600 mt-1">Check back after your instructor submits and grades are approved.</p>
            </div>
          )}
          {grades.length > 0 && (
            <div className="space-y-3">
              {grades.map((g, idx) => (
                <div key={g.final_grade_id || idx}
                  data-testid={`grade-row-${g.final_grade_id || idx}`}
                  className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex items-start justify-between">
                  <div>
                    <p className="text-sm font-bold text-white">{g.course_title || "Course"}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{g.term_code} · Section {g.section_code}</p>
                    {g.computed_pct != null && (
                      <p className="text-xs text-slate-500 mt-1">Score: {parseFloat(g.computed_pct).toFixed(1)}%</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-black text-amber-400">{g.grade_code || "—"}</div>
                    <div className="text-xs text-slate-400 mt-0.5">Final Grade</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN: Role-aware router
// ─────────────────────────────────────────────────────────────────────────────

export default function Gradebook() {
  const { user } = useAuth();
  const role = user?.role || "";

  const isAdmin = ["super_admin", "institution_admin", "registrar", "programme_manager", "dean"].includes(role);
  const isFaculty = ["faculty", "instructor"].includes(role);
  const isStudent = role === "student";

  if (!role) return <div className="py-16 text-center"><Spinner /></div>;
  if (isAdmin) return <GradebookAdmin />;
  if (isFaculty) return <GradebookFaculty />;
  if (isStudent) return <GradebookStudent />;

  return (
    <div className="text-center py-16">
      <p className="text-slate-400">Gradebook is not available for your current role ({role}).</p>
    </div>
  );
}
